import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { useRef } from 'react';
import { FaTimes } from 'react-icons/fa';

import TopNavbar from '../top-navbar/TopNav';
import '../../styles/employee/employment-check-physical.css';
import { toast } from 'react-toastify';
import { getEmploymentPhysical, savePhysicalEmployment } from '../../services/employee/api';

const EmploymentCheckPhysical = ({ candidateId, employmentInfos, onBack }) => {
  const [employmentDetails, setEmploymentDetails] = useState([]);
  const [overallVerificationStatus, setOverallVerificationStatus] = useState(false);
  const [overallRemarks, setOverallRemarks] = useState('');
  const [loading, setLoading] = useState(true);
const fileInputRefs = useRef({});

  const token = localStorage.getItem('bgc_token');

  const fetchEmploymentDetails = async () => {
  try {
    const response = await getEmploymentPhysical(candidateId);

    const data = response.data;

    if (Array.isArray(data?.data) && data.data.length > 0) {
      const formattedData = data.data.map((detail) => ({
        ...detail,
        employmentProof: null, 
        uploadedProofUrl: detail.employmentProofFileName || null,
      }));
      setEmploymentDetails(formattedData);
      setOverallVerificationStatus(data.overallVerificationStatus || false);
      setOverallRemarks(data.data[0]?.remarks || "");
    } else {
      applyEmploymentFallback();
    }
  } catch (error) {
    toast.error(
      error.response?.data?.message ||
      "Error fetching employment details"
    );
    applyEmploymentFallback();
  } finally {
    setLoading(false);
  }
};

const triggerFileUpload = (index) => {
  fileInputRefs.current[index]?.click();
};

const removeUploadedFile = (index) => {
  const updatedDetails = [...employmentDetails];
  updatedDetails[index].employmentProof = null;
  updatedDetails[index].uploadedProofUrl = null;
  setEmploymentDetails(updatedDetails);
};

const applyEmploymentFallback = () => {
  setEmploymentDetails(
    employmentInfos.map((info) => ({
      ...info,
      employmentProof: null,
      uploadedProofUrl: null,
    }))
  );
};

  useEffect(() => {
    fetchEmploymentDetails();
  }, [candidateId]);

  /** Handle file upload */
  const handleFileUpload = (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Replace previous uploaded file with new one
    const updatedDetails = [...employmentDetails];
    updatedDetails[index].employmentProof = file;
    updatedDetails[index].uploadedProofUrl = URL.createObjectURL(file); // preview

    setEmploymentDetails(updatedDetails);
  };

  /** Validation: remarks required + at least one proof must exist */
  const validateForm = () => {
    const hasAnyProof = employmentDetails.some(
      (detail) => detail.employmentProof || detail.uploadedProofUrl
    );

    if (!hasAnyProof) {
      toast.error('At least one employment proof must be uploaded.');
      return false;
    }

    if (!overallRemarks.trim()) {
      toast.error('Remarks are mandatory.');
      return false;
    }

    return true;
  };

  /** Handle form submit */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const formData = new FormData();
    formData.append('candidateId', candidateId);
    formData.append('overallVerificationStatus', overallVerificationStatus);
    formData.append('overallRemarks', overallRemarks);

    const employmentDataToSend = employmentDetails.map((detail) => ({
      id: detail.id,
      employeeCode: detail.employeeCode,
      companyName: detail.companyName,
      jobTitle: detail.jobTitle,
      location: detail.location,
      hrContactNumber: detail.hrContactNumber,
      hrContactEmail: detail.hrContactEmail,
      workingFrom: detail.workingFrom,
      workingTo: detail.workingTo,
    }));

    formData.append('employmentDetails', JSON.stringify(employmentDataToSend));

    // Append new files
    employmentDetails.forEach((detail) => {
      if (detail.employmentProof) {
        formData.append(`employmentFiles.${detail.employeeCode}`, detail.employmentProof);
      }
    });

    try {
      const response = await savePhysicalEmployment(formData);
      toast.success("Employment verification submitted successfully!");
      onBack();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to verify employment.";
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    onBack();
  };

  if (loading) {
    return <div className="loading">Loading employment details...</div>;
  }

  return (
    <div className="dashboard-app-container">
      <Sidebar />
      <div className="dashboard-right-panel">
        <TopNavbar />
        <div className="emp-header">
          <h2>Employment Check Physical</h2>
        </div>

        <div className="employment-status-container">
          <form onSubmit={handleSubmit}>
            {employmentDetails.map((details, index) => (
              <div key={index} className="employment-details-section">
                <h3>Employment Details </h3>

                {/* Employee Details */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Employee Code</label>
                    <input type="text" value={details.employeeCode || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label>Company Name</label>
                    <input type="text" value={details.companyName || ''} disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Designation</label>
                    <input type="text" value={details.jobTitle || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" value={details.location || ''} disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>HR Contact Number</label>
                    <input type="text" value={details.hrContactNumber || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label>HR Contact Email</label>
                    <input type="email" value={details.hrContactEmail || ''} disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Working From</label>
                    <input type="text" value={details.workingFrom || ''} disabled />
                  </div>
                  <div className="form-group">
                    <label>Working To</label>
                    <input type="text" value={details.workingTo || ''} disabled />
                  </div>
                </div>

                {/* File Upload */}
                <div className="form-row file-upload-row">
                  <div className="form-group file-upload-group full-width">
                    <label>Employment Proof</label>

<div className="upload-certificate-group">

  <input
    type="file"
    ref={(el) => (fileInputRefs.current[index] = el)}
    style={{ display: "none" }}
    accept="image/jpeg,image/png,application/pdf"
    onChange={(e) => handleFileUpload(e, index)}
  />

  <div
    className="upload-certificate-button"
    onClick={() => triggerFileUpload(index)}
  >
    Drag & Drop or Click to Upload
  </div>

  {(details.employmentProof || details.uploadedProofUrl) && (
    <div className="file-list">
      <span className="file-list-item">
        {details.employmentProof
          ? details.employmentProof.name
          : details.uploadedProofUrl.split('/').pop()}

        <FaTimes
          className="remove-file-icon"
          onClick={() => removeUploadedFile(index)}
        />
      </span>
    </div>
  )}

  <p className="upload-certificate-note">
    * File must be PDF / JPG / PNG and less than 5MB
  </p>

</div>


                  </div>
                </div>
              </div>
            ))}

            {/* Remarks */}
            <div className="form-row remarks-row">
              <div className="form-group full-width">
                <label>Remarks*</label>
                <textarea
                  value={overallRemarks}
                  onChange={(e) => setOverallRemarks(e.target.value)}
                  placeholder="Add remarks here..."
                />
              </div>
            </div>

            {/* Verification Checkbox */}
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={overallVerificationStatus}
                  onChange={(e) => setOverallVerificationStatus(e.target.checked)}
                />{' '}
                Verification Status
              </label>
              <p className="verification-note">
                            ⚠️ Once verified, this step will be locked and you cannot return to modify the Physical Employment details.
              </p>
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="submit-button">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmploymentCheckPhysical;